import {BIRTHDAY_COUPON_CODES} from '../constants'
import {isAddressNameValid, isAddressPhoneValid} from './address-utils'

export const getTotalQuantity = (basket, changeUpProductID) => {
    let totalQuantity = 0
    basket?.productItems?.forEach((lineItem) => {
        const isHiddenProductLineitem =
            lineItem.c_hiddenFluidProduct || (lineItem && lineItem.productId === changeUpProductID)
        if (!isHiddenProductLineitem) {
            totalQuantity += lineItem.quantity
        }
    })
    if (basket?.giftCertificateItems?.length) {
        totalQuantity += basket.giftCertificateItems.length
    }
    return totalQuantity
}

/** True if the given coupon code is a birthday coupon (BA25 or BA50). */
export const isBirthdayCouponCode = (code) => Boolean(code && BIRTHDAY_COUPON_CODES.includes(code))

/**
 * True if the basket has at least one product line with the given coupon code in its priceAdjustments.
 * @param {Object} basket - basket object
 * @param {string} couponCode - coupon code (e.g. BA25, BA50)
 * @returns {boolean}
 */
export const hasBasketLineWithCouponCode = (basket, couponCode) =>
    Boolean(
        couponCode &&
            basket?.productItems?.some((pi) =>
                pi.priceAdjustments?.some((pa) => pa.couponCode === couponCode)
            )
    )

export const getBasketProductIds = (basket) =>
    basket?.productItems
        ?.filter(
            (item) =>
                !item.bonusProductLineItem &&
                !item.bonusDiscountLineItemId &&
                !item.c_hiddenFluidProduct &&
                item.productId
        )
        ?.map((item) => item.productId) ?? []

const getLineItemSpend = (lineItem) =>
    lineItem.quantity *
    ((lineItem.c_gePriceAfterDiscount ?? lineItem?.priceAfterItemDiscount ?? lineItem.price ?? 0) /
        lineItem.quantity)

const getQualifyingGwpLineItemSpend = (lineItem) => {
    if (lineItem.c_gePriceAfterDiscount == null) {
        return 0
    }

    return lineItem.c_gePriceAfterDiscount
}

export const productQualifiesForPromotion = (product, promoId) => {
    if (!product || !promoId) {
        return false
    }

    const matchesPromo = (promotion) =>
        promotion?.promotionId === promoId || promotion?.id === promoId

    return product.productPromotions?.some(matchesPromo) || product.c_promotions?.some(matchesPromo)
}

export const getQualifyingProductIds = (products, promoId) => {
    const ids = new Set()

    if (!promoId || !products?.length) {
        return ids
    }

    products.forEach((product) => {
        if (productQualifiesForPromotion(product, promoId)) {
            ids.add(product.id)
        }
    })

    return ids
}

export const isQualifyingGwpLineItem = (lineItem, qualifyingProductIds) =>
    Boolean(
        lineItem?.productId &&
            !lineItem.bonusProductLineItem &&
            !lineItem.bonusDiscountLineItemId &&
            !lineItem.c_hiddenFluidProduct &&
            qualifyingProductIds?.has(lineItem.productId)
    )

export const getGwpQualifyingSpend = (basket, promoId, qualifyingProductIds) => {
    if (qualifyingProductIds !== undefined) {
        return (
            basket?.productItems
                ?.filter((item) => isQualifyingGwpLineItem(item, qualifyingProductIds))
                ?.reduce((total, item) => total + getLineItemSpend(item), 0) ?? 0
        )
    }

    return (
        basket?.productItems?.reduce(
            (total, item) => total + getQualifyingGwpLineItemSpend(item),
            0
        ) ?? 0
    )
}

export const getGwpMeterData = (currentBasket, promoConfig, promoId, qualifyingProductIds) => {
    let activeLevel = 0
    const basketTotal = getGwpQualifyingSpend(currentBasket, promoId, qualifyingProductIds)

    const updatedLevels = promoConfig.levels?.map((level, index) => {
        const isReached = basketTotal >= level.threshold
        const remainingAmount = isReached ? 0 : level.threshold - basketTotal

        if (isReached) {
            activeLevel = index + 1
        }

        return {
            ...level,
            reached: isReached,
            remaining: remainingAmount?.toFixed(2)
        }
    })
    const currentLevelData = updatedLevels?.[activeLevel > 0 ? activeLevel - 1 : 0]
    return currentLevelData
}

/**
 * Count selected bonus quantity for a bonusDiscountLineItemId by summing quantities
 * of bonus product line items in the basket.
 *
 * @param {Object} basket
 * @param {string} bonusDiscountLineItemId
 * @returns {number}
 */
export const getSelectedBonusQuantityForBonusDiscountLineItem = (basket, bonusDiscountLineItemId) =>
    (basket?.productItems || [])
        .filter(
            (pi) =>
                pi?.bonusProductLineItem &&
                bonusDiscountLineItemId &&
                pi?.bonusDiscountLineItemId === bonusDiscountLineItemId
        )
        .reduce((sum, pi) => sum + (pi?.quantity || 0), 0)

/**
 * Remaining bonus quantity for a given bonusDiscountLineItem.
 *
 * @param {Object} basket
 * @param {Object} bonusDiscountLineItem
 * @returns {number}
 */
export const getRemainingBonusQuantityForBonusDiscountLineItem = (
    basket,
    bonusDiscountLineItem
) => {
    const maxBonusItems = bonusDiscountLineItem?.maxBonusItems || 0
    const selected = getSelectedBonusQuantityForBonusDiscountLineItem(
        basket,
        bonusDiscountLineItem?.id
    )
    return Math.max(0, maxBonusItems - selected)
}

/**
 * True if there are bonus items available that have not yet been selected.
 *
 * @param {Object} basket
 * @returns {boolean}
 */
export const hasUnredeemedBonusDiscountLineItems = (basket) =>
    Boolean(
        (basket?.bonusDiscountLineItems || []).some(
            (bdi) => getRemainingBonusQuantityForBonusDiscountLineItem(basket, bdi) > 0
        )
    )

export const getCharitableDonationItem = (basket, changeUpProductID) => {
    return basket?.productItems?.find((item) => item?.productId === changeUpProductID)
}

export const getSubTotal = (basket, changeUpProductID = '') => {
    let subTotal = basket?.productSubTotal || basket?.productTotal || 0
    // Add gift certificate amounts
    if (basket?.giftCertificateItems?.length) {
        subTotal += basket.giftCertificateItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    }

    // Subtract charitable donation price
    const charitableDonationItem = getCharitableDonationItem(basket, changeUpProductID)
    const charitableDonationPrice = charitableDonationItem
        ? charitableDonationItem.price ||
          charitableDonationItem.priceAfterItemDiscount ||
          charitableDonationItem.c_gePriceAfterDiscount
        : 0
    return subTotal - charitableDonationPrice
}

/**
 * Monetary `value` for the GA4 purchase event. This is the merchandise total
 * after all product- and order-level discounts and excluding tax, plus gift
 * certificates, minus the donation.
 *
 * @param {Object} order - order object from useOrder
 * @param {string} changeUpProductID - configured ChangeUp donation product id
 * @returns {number} value rounded to 2 decimals
 */
export const getPurchaseValue = (order, changeUpProductID = '') => {
    let value = order?.productTotal ?? order?.productSubTotal ?? 0

    if (order?.giftCertificateItems?.length) {
        value += order.giftCertificateItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    }

    const charitableDonationItem = getCharitableDonationItem(order, changeUpProductID)
    const charitableDonationPrice = charitableDonationItem
        ? charitableDonationItem.price ||
          charitableDonationItem.priceAfterItemDiscount ||
          charitableDonationItem.c_gePriceAfterDiscount ||
          0
        : 0

    return Number((value - charitableDonationPrice).toFixed(2))
}

export const getOutOfStockProductsList = (products, basket) => {
    let outOfStockProductsList = []
    if (products && basket?.productItems?.length) {
        basket.productItems.forEach((product) => {
            const selectedProd = products[product.productId]
            if (selectedProd?.inventory.stockLevel < product.quantity) {
                outOfStockProductsList.push(product.productId)
            }
        })
    }
    return outOfStockProductsList
}

export const getGlobaleRestrictedList = (products, countryISO) => {
    if (!products || countryISO === 'US') return []
    try {
        return Object.values(products)
            ?.filter((product) => {
                const isForbidden = product.c_geIsForbidden === true

                const restrictedCountries = product.c_geRestrictedCountries
                    ? product.c_geRestrictedCountries.split(',')
                    : []

                const isCountryRestricted = restrictedCountries?.includes(countryISO)

                return isForbidden || isCountryRestricted
            })
            ?.map((product) => product.id)
    } catch (e) {
        console.error('Error occurred in getGlobaleRestrictedList', e.message)
    }
}

export const isAnyCbProductOutOfStock = (products, basket) => {
    if (products && basket?.productItems?.length) {
        return (
            basket?.productItems?.some((item) => {
                if (!item.c_fluidConfigureMetadata) return false

                let meta
                try {
                    meta = JSON.parse(item.c_fluidConfigureMetadata)
                } catch {
                    return false
                }

                const idsToCheck = [meta.metal, ...(meta.stones || [])]
                return idsToCheck.some((id) => products?.[id]?.inventory?.stockLevel < 1)
            }) || false
        )
    }
    return false
}

export const isColorBarMainProduct = (product) =>
    Boolean(product?.c_fluidConfigureMetadata && !product?.c_hiddenFluidProduct)

export const parseColorBarMetadata = (product) => {
    if (!product?.c_fluidConfigureMetadata) return null
    try {
        return JSON.parse(product.c_fluidConfigureMetadata)
    } catch {
        return null
    }
}

export const getColorBarLinkedItems = (product, basketProductItems) => {
    const metadata = parseColorBarMetadata(product)
    if (!metadata) return []

    const linkedSkus = new Set([metadata.metal, ...(metadata.stones || [])].filter(Boolean))

    return (basketProductItems || []).filter(
        (item) => item.c_hiddenFluidProduct && linkedSkus.has(item.productId)
    )
}

const getStoneQuantity = (mainProductQuantity, metadata, basketProductItems) => {
    if (!metadata?.stones?.length || !mainProductQuantity) return 1

    const firstStone = (basketProductItems || []).find(
        (item) => item.c_hiddenFluidProduct && item.productId === metadata.stones[0]
    )
    if (!firstStone) return 1

    const ratio = Math.round(firstStone.quantity / mainProductQuantity)
    return ratio > 0 ? ratio : 1
}

export const getColorBarLinkedItemUpdates = (product, newQuantity, basketProductItems) => {
    const metadata = parseColorBarMetadata(product)
    if (!metadata) return []

    const stoneQuantity = getStoneQuantity(product?.quantity, metadata, basketProductItems)
    const updates = []

    const metalItem = (basketProductItems || []).find(
        (item) => item.c_hiddenFluidProduct && item.productId === metadata.metal
    )
    if (metalItem) {
        updates.push({
            itemId: metalItem.itemId,
            productId: metalItem.productId,
            quantity: newQuantity
        })
    }

    for (const stoneSku of metadata.stones || []) {
        const stoneItem = (basketProductItems || []).find(
            (item) => item.c_hiddenFluidProduct && item.productId === stoneSku
        )
        if (stoneItem) {
            updates.push({
                itemId: stoneItem.itemId,
                productId: stoneItem.productId,
                quantity: newQuantity * stoneQuantity
            })
        }
    }

    return updates
}

export const hasBopisError = (basket) => {
    let hasError = false
    basket?.productItems?.forEach((productItem) => {
        if (!productItem?.c_oosAtStore) {
            hasError = productItem?.c_oosAtStore
        }
    })
    return hasError
}

export const isDeliveryAddressFilled = (basket) => {
    const shippingAddress = basket?.shipments?.[0]?.shippingAddress
    const {address1, city, countryCode, postalCode, firstName, lastName, stateCode} =
        shippingAddress || {}
    return (
        address1 &&
        city &&
        countryCode &&
        postalCode &&
        firstName &&
        lastName &&
        stateCode &&
        // Bolt permits single-character names and non-US phones that get cropped into
        // the form. Treat those addresses as incomplete so the shipping step stays open.
        isAddressNameValid(shippingAddress) &&
        isAddressPhoneValid(shippingAddress)
    )
}

export const isEGiftCardInBasket = (basket) => {
    return !!basket?.giftCertificateItems?.length && !basket?.productItems?.length
}

export const isFineJewelryExists = (products) => {
    if (!products) {
        return false
    }
    return Object.values(products)?.some((p) => p.c_orderHandleType?.includes('isFineJewelry'))
}

export const checkIfSavedAddressUsed = (shippingAddress, defaultAddress) => {
    if (!shippingAddress || !defaultAddress) {
        return false
    }

    const fieldsToCompare = [
        'firstName',
        'lastName',
        'address1',
        'address2',
        'city',
        'postalCode',
        'stateCode'
    ]

    return fieldsToCompare.every((field) => shippingAddress[field] === defaultAddress[field])
}

export const checkIfShippingAddressingIsMissing = (shippingAddress) => {
    const {firstName, lastName, address1, city, stateCode, postalCode} = shippingAddress || {}
    return !!firstName && !!lastName && !!address1 && !!city && !!stateCode && !!postalCode
}

const normalizePostalCode = (postalCode) => postalCode?.split('-')[0]

export const checkIfBillingEqualsShipping = (shippingAddress, billingAddress) => {
    if (!shippingAddress || !billingAddress || !billingAddress.firstName) {
        return true
    }

    const fieldsToCompare = [
        'firstName',
        'lastName',
        'address1',
        'address2',
        'city',
        'postalCode',
        'stateCode'
    ]

    return fieldsToCompare.every((field) => {
        if (field === 'postalCode') {
            return (
                normalizePostalCode(shippingAddress[field]) ===
                normalizePostalCode(billingAddress[field])
            )
        }

        return shippingAddress[field] === billingAddress[field]
    })
}
